'use client';

import { useState } from 'react';
import Link from 'next/link';
import { motion } from 'motion/react';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForgotPasswordMn } from '~/lib/api/mutations';
import { AuthContainer } from '~/components/auth/AuthContainer';
import { CheckCircle, Loader, Mail, Send } from 'lucide-react';
import { Button } from '~/components/ui';
import { useAppRouter } from '~/hooks';

// Zod schema for signup form validation
const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const router = useAppRouter();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const forgotPasswordMn = useForgotPasswordMn({
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    values: {
      email: '',
    },
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur', // Validate on blur for better UX
  });

  const handleForgotPassword = async (values: ForgotPasswordFormData) => {
    forgotPasswordMn.mutate({ email: values.email });
  };

  const handleResendEmail = async () => {
    if (!forgotPasswordMn.data?.email) {
      setError('No email found');
      return;
    }
    forgotPasswordMn.mutate({ email: forgotPasswordMn.data?.email });
  };

  if (success) {
    return (
      <AuthContainer>
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full mb-4"
          >
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </motion.div>

          <h1 className="mb-3 text-foreground">Check Your Email</h1>
          <p className="text-foreground-secondary mb-6">
            We&apos;ve sent password reset instructions to{' '}
            <span className="text-primary">{forgotPasswordMn.data?.email}</span>
          </p>

          <div className="space-y-3">
            <Button
              onClick={() => router.push('/auth/signin')}
              className="w-full rounded-lg transition-opacity shadow-lg"
            >
              Back to Login
            </Button>

            <Button
              variant="secondary"
              onClick={handleResendEmail}
              disabled={forgotPasswordMn.isPending}
              className="w-full rounded-lg transition-colors"
            >
              Resend Email
            </Button>
          </div>

          <p className="text-foreground-secondary mt-6">
            Didn&apos;t receive the email? Check your spam folder or try again.
          </p>
        </div>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer
      title="Reset Your Password"
      description="Enter your email and we'll send you a reset link"
    >
      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          <i className="fas fa-exclamation-circle mr-2" />
          {error}
        </div>
      )}

      {/* Form */}

      <form onSubmit={handleSubmit(handleForgotPassword)} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-foreground mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              className="w-full pl-10 pr-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              {...register('email')}
            />
            {errors.email && (
              <span className="text-red-500">{errors.email.message}</span>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2"
          disabled={forgotPasswordMn.isPending}
        >
          {forgotPasswordMn.isPending ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Sending reset link...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Send Reset Link
            </>
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-foreground-secondary">
          Remember your password?{' '}
          <Link href="/auth/signin" className="text-primary hover:opacity-80">
            Sign in
          </Link>
        </p>
      </div>
    </AuthContainer>
  );
}
